import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Action, BlueprintDeployment, BlueprintDeploymentState } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

const isDeploymentDeleteDisabled = (state: BlueprintDeploymentState): boolean => {
  return [
    BlueprintDeploymentState.InProgress,
    BlueprintDeploymentState.Unconfirmed,
    BlueprintDeploymentState.None,
  ].includes(state);
};

const getDeleteDisabledMessage = (state: BlueprintDeploymentState): string | undefined => {
  switch (state) {
    case BlueprintDeploymentState.InProgress:
      return "Cannot delete deployment while it is in progress";
    case BlueprintDeploymentState.Unconfirmed:
      return "Cannot delete deployment while it is unconfirmed";
    case BlueprintDeploymentState.None:
      return "Cannot delete deployment while it is in progress";
    case BlueprintDeploymentState.Destroying:
    case BlueprintDeploymentState.DestroyingFailed:
    case BlueprintDeploymentState.Discarded:
    case BlueprintDeploymentState.Failure:
    case BlueprintDeploymentState.Finished:
      return undefined;
  }
};

const DELETE_DEPLOYMENT_ACCESS_TYPES = [Action.SpaceAdmin, Action.DeleteBlueprintDeployment];

/**
 * Hook that returns a function to check if the current user can delete a deployment.
 * A user needs both the DeleteBlueprintDeployment permission action AND write access (or admin) to the template's space to delete a deployment.
 * A user can't delete a deployment that is destroying, in progress, or unconfirmed.
 * @returns {(deployment: BlueprintDeployment, spaceId: string) => AccessResult} Function that takes a deployment and space ID and returns an AccessResult indicating if user can delete it
 */
const useCanDeleteDeployment = (): ((
  deployment: BlueprintDeployment,
  spaceId: string
) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (deployment: BlueprintDeployment, spaceId: string) => {
      const hasDeletePermission = hasAtLeastOneRequiredAction(
        DELETE_DEPLOYMENT_ACCESS_TYPES,
        viewer,
        spaceId
      );

      if (!hasDeletePermission) {
        return { result: false, message: NO_ACCESS_MESSAGE };
      }

      if (isDeploymentDeleteDisabled(deployment.state)) {
        const message = getDeleteDisabledMessage(deployment.state);
        return {
          result: false,
          message,
        };
      }

      return { result: true };
    },
    [viewer]
  );
};

export default useCanDeleteDeployment;
